/**
 * @file src/offline/uiAssetPrecache.js
 * @description The design library's icons, stored so an offline boot has a face.
 *
 * Precaching the build does not reach these. The icons are not in the bundle: the library ships a
 * baked NAME LIST and fetches each glyph's SVG from the herbarium at render time, plus a manifest
 * that may override the list. Everything else about an offline boot can be arranged in advance from
 * the release payloads; this cannot, because nothing in the collection mentions it.
 *
 * It is worth doing rather than degrading, unlike the other off-origin hosts, for one reason: an
 * icon is not decoration in this app. Play, pause, loop, the track-type marks and the whole
 * transport are icon-only controls with no text beside them, so a boot that cannot draw them is not
 * a boot that looks plainer — it is a player whose buttons are blank squares.
 *
 * ## Why this warms a worker route instead of writing its own cache
 *
 * The library fetches icons itself, with a plain `fetch`, from code this app does not call. Bytes
 * written into a cache of our own would sit there unread. So the service worker owns an icon route
 * and this module simply ASKS for each icon once — the request passes through the worker, which
 * stores it. One mechanism, and the thing that stores the bytes is the same thing that will later
 * serve them.
 *
 * ## Why the URLs here carry no query string
 *
 * The library requests the manifest with a cache-buster (`?t=<now>`) and each icon with a version
 * (`?v=<manifest version>`), and neither is stable across builds or reloads. The worker's route
 * therefore matches on the PATH alone, and these requests are made bare so what is stored is what
 * will be looked for. That is a fact about the worker route, and the two must stay in step — see
 * `sw-icon-route` in `vite.config.js`.
 */
import { ICONS, ICON_BASE_URL, ICON_NAMES } from 'plantasia.space-design/icons';
import { getHerbariumBase } from '../utils/cdnAssets.js';
import { isOfflineWorkerControlling } from './registerOfflineWorker.js';

/** The library's own manifest path. Not exported by the package, so it is named once, here. */
const ICON_MANIFEST_PATH = '/assets/icons/manifest.json';

/**
 * How long the whole icon warm-up may take before the download gets on with the audio.
 *
 * A request that never settles is not the same as one that fails: `fetch` does not time out on its
 * own, so one hung connection to the CDN left the download parked in its `interface` phase with no
 * audio started and no error, forever. Icons are a nicety; the audio is the download.
 */
const UI_ASSET_BUDGET_MS = 20000;

/** Where the icons actually live for THIS deployment — dev herbarium in dev, prod in prod. */
function resolveIconBase() {
  const configured = getHerbariumBase();
  const base = typeof configured === 'string' && configured.trim() ? configured.trim() : ICON_BASE_URL;
  return String(base).replace(/\/+$/, '');
}

function absolute(base, path) {
  if (typeof path !== 'string' || !path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Every icon URL this app could ask for, plus the manifest.
 *
 * The baked list is the floor. The manifest is fetched first because it may RENAME paths — a live
 * manifest with a moved icon would otherwise leave that one glyph blank offline while every other
 * one worked, which is the most confusing possible outcome. A manifest that will not load is not an
 * error: the library falls back to the baked paths in exactly that case, so the baked list is
 * already the right answer.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<string[]>} absolute URLs, de-duplicated.
 */
export async function resolveUiAssetUrls({ signal } = {}) {
  const base = resolveIconBase();
  const urls = new Set();

  const manifestUrl = absolute(base, ICON_MANIFEST_PATH);
  if (manifestUrl) urls.add(manifestUrl);

  for (const name of ICON_NAMES) {
    const url = absolute(base, ICONS?.[name]?.path || `/assets/icons/${name}.svg`);
    if (url) urls.add(url);
  }

  try {
    // The signal goes in here too. Without it, cancelling a download during the interface phase
    // still waited out this request before the abort was observed.
    const response = await fetch(manifestUrl, { signal });
    if (response.ok) {
      const manifest = await response.json();
      const prefix = typeof manifest?.iconPrefix === 'string' ? manifest.iconPrefix : '/assets/icons';
      for (const [name, entry] of Object.entries(manifest?.icons || {})) {
        const url = absolute(base, entry?.path || `${prefix}/${name}.svg`);
        if (url) urls.add(url);
      }
    }
  } catch {
    // The baked list stands, which is what the library itself falls back to.
  }

  return [...urls];
}

/**
 * Ask for every icon once, so the worker's icon route stores it.
 *
 * Failures are counted, never thrown. This runs as part of a download, and a collection whose audio
 * and textures are all here is downloaded — refusing it because a CDN dropped one glyph would trade
 * a playable collection for a tidy icon set. The caller reports what was missed and moves on.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ requested: number, stored: number, failed: number, skipped?: string }>}
 */
export async function precacheUiAssets({ signal } = {}) {
  // Nothing stores these unless a worker is CONTROLLING this page. The mechanism is "ask, and the
  // worker's icon route keeps the answer" — with no worker in front of the page the requests go
  // straight to the network, are kept by nobody, and cost a download seventy-odd pointless round
  // trips. A registration is not enough: the very first load after an install has one and no
  // controller, and serves nothing.
  if (!isOfflineWorkerControlling()) {
    return { requested: 0, stored: 0, failed: 0, skipped: 'no-worker' };
  }

  // A budget of its own, folded together with the caller's cancel. Either can end this.
  const budget = new AbortController();
  const timer = setTimeout(() => budget.abort(), UI_ASSET_BUDGET_MS);
  const onCallerAbort = () => budget.abort();
  signal?.addEventListener('abort', onCallerAbort, { once: true });

  try {
    return await warmIcons(budget.signal, signal);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * The warm-up itself. `budgetSignal` ends it; `callerSignal` says whether ending it was a CANCEL —
 * a cancelled download must propagate, a timed-out icon fetch must not.
 */
async function warmIcons(budgetSignal, callerSignal) {
  const signal = budgetSignal;
  const urls = await resolveUiAssetUrls({ signal }).catch(() => []);
  let stored = 0;
  let failed = 0;

  // Small files on one host: a handful at a time keeps the connection busy without opening
  // seventy-odd sockets against a CDN in the middle of a byte download that also wants bandwidth.
  const CONCURRENCY = 6;
  let cursor = 0;

  async function worker() {
    while (cursor < urls.length) {
      if (signal?.aborted) return;
      const url = urls[cursor];
      cursor += 1;
      try {
        const response = await fetch(url, { signal });
        if (response.ok) stored += 1;
        else failed += 1;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        failed += 1;
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker));
  // Only the CALLER's cancel is a cancel. Running out of the icon budget is a normal, partial
  // outcome — the download carries on to the bytes that matter.
  if (callerSignal?.aborted) throw new DOMException('Download cancelled', 'AbortError');
  return { requested: urls.length, stored, failed, timedOut: budgetSignal.aborted };
}
